import { z } from 'zod';
/**
 * Word Ladder — vocabulary + search.
 *
 * `cat → cot → cog → dog`. Change exactly one letter per step. Every rung must
 * be a real word. Reach the target.
 *
 * Both players get the same seeded start and target.
 *
 * WHY THIS GAME EARNS ITS PLACE: it is the first minigame whose progress rail
 * genuinely moves. Wordle and Nerdle are binary — solved, or +60s — so a player
 * one guess from winning shows the same marker as one who never started. Here
 * every rung you climb is visible to your opponent.
 *
 * Scoring:
 *   - a rejected word is FREE. It already cost you the seconds you spent typing
 *     it. Tile Tap's miss penalty exists because spam-clicking was an exploit;
 *     there is no equivalent here, because the dictionary gate makes brute
 *     forcing a rung impossible. Penalising exploration in a game *about*
 *     exploration would be perverse.
 *   - incompletion costs `(1 - progress) * failedSolvePenaltyMs`, graded.
 */
/** Rungs are 4 or 5 letters, matching the shipped Wordle dictionary. */
export const MIN_WORD_LENGTH = 4;
export const MAX_WORD_LENGTH = 5;
export const RULES_NOTE = 'VOCABULARY + SEARCH';
export const RULES_HINT = 'Change one letter — must be a common word';
/* -------------------------------------------------------------------------- */
/* Client -> server events                                                     */
/* -------------------------------------------------------------------------- */
/**
 * Climb one rung.
 *
 * The schema bounds the shape; the ENGINE enforces the exact length for this
 * segment, that the word is in the dictionary, and that it differs from the
 * current rung by exactly one letter. Encoding those rules on the wire would
 * put game logic in the contract.
 */
export const WordLadderStepEvent = z.object({
    type: z.literal('step'),
    word: z
        .string()
        .trim()
        .toLowerCase()
        .min(MIN_WORD_LENGTH)
        .max(MAX_WORD_LENGTH)
        .regex(/^[a-z]+$/, 'Rung must be letters a-z'),
});
/**
 * Undo the last rung.
 *
 * Free, and it cannot lose you progress: `progress` is a high-water mark of the
 * closest you ever got. Backing out of a wrong branch is part of the search.
 */
export const WordLadderUndoEvent = z.object({
    type: z.literal('undo'),
});
/**
 * Buy a hint: reveals the next word on an optimal path from where you stand.
 *
 * Not a random word — the server knows the true distance to the target from
 * every word, so it can always name a rung that gets strictly closer.
 */
export const WordLadderHintEvent = z.object({
    type: z.literal('hint'),
});
export const WordLadderEvent = z.discriminatedUnion('type', [
    WordLadderStepEvent,
    WordLadderUndoEvent,
    WordLadderHintEvent,
]);
//# sourceMappingURL=word-ladder.js.map